import { Table, TableBody, Typography } from '@material-ui/core';
import { debounce, get, isEmpty, round, size } from 'lodash';
import React, { useState } from 'react';
import { TableCell, TableRow } from 'ui-library/table';
import { TableRowInput } from './TableRowInput';
import { SupplierSearchModal } from './SupplierSearchModal';
import { UserServiceContractsDtosUserProfileDto } from 'api/types';

const gstRate = 0.1;
const gstPoints = 1 / gstRate;
export interface InvoiceInputFields {
	supplier?: UserServiceContractsDtosUserProfileDto;
	billerCode?: string;
	CRN?: string;
	dueDate?: string;
	totalAmount?: number;
	amount?: number;
	GST?: number;
}

type InvoiceFieldsProps = InvoiceInputFields & {
	onChange: (s: InvoiceInputFields) => void;
	officeEntityId?: string;
};

const calcExpr = (val: number | undefined, func: (n: number) => number) => (val === undefined ? undefined : func(val));

export const InvoiceFields: React.FC<InvoiceFieldsProps> = ({ officeEntityId, onChange, ...fields }) => {
	const [invoiceFields, setInvoiceFields] = useState<InvoiceInputFields>(fields);
	const [open, setOpen] = React.useState(false);

	const recalc = (field: string, _value?: string) => {
		const _v = isEmpty(_value) ? undefined : parseFloat(_value!);

		if (_v === undefined) return {};

		let totalAmount: number | undefined = undefined;
		let GST: number | undefined = undefined;
		let amount: number | undefined = undefined;

		switch (field) {
			case 'totalAmount': {
				totalAmount = _v;
				amount = calcExpr(totalAmount, (value) => round(value / (1 + gstRate), 2));
				GST = calcExpr(totalAmount, (value) => round((value / (1 + gstRate)) * gstRate, 2));
				break;
			}
			case 'amount': {
				amount = _v;
				totalAmount = calcExpr(amount, (value) => round(value + _v * gstRate, 2));
				GST = calcExpr(amount, (value) => round(value * gstRate, 2));
				break;
			}
			case 'GST': {
				GST = _v;
				amount = calcExpr(GST, (value) => round(value * gstPoints + value, 2) - _v);
				totalAmount = calcExpr(GST, (value) => round(value * gstPoints + value, 2));
				break;
			}
			default:
				break;
		}

		return {
			amount: round(amount!, 2),
			GST: round(GST!, 2),
			totalAmount: round(totalAmount!, 2)
		};
	};

	const fireOnChange = debounce((fields: InvoiceInputFields) => {
		onChange(fields);
	}, 100);

	const onFieldChange = (field: keyof InvoiceInputFields) => (e: React.SyntheticEvent) => {
		const value: string | undefined = get(e, 'target.value', undefined);
		let calculated = {};
		if (['amount', 'GST', 'totalAmount'].includes(field)) {
			calculated = recalc(field, value);
		}
		const newInvoiceFields = { ...invoiceFields, [field]: value, ...calculated };
		setInvoiceFields(newInvoiceFields);
		fireOnChange(newInvoiceFields);
	};

	const concat = (label: string, to: string, from?: string | null) => {
		let outv = '';
		if (!from || size(from) == 0) return to;
		if (size(to) === 0) outv = `${label}: ${from}`;
		if (size(to) > 0) outv = `${to} - ${label}: ${from}`;
		return outv;
	};

	const getSupplierName = () => {
		if (!invoiceFields?.supplier) return undefined;

		let supplierName = '';
		supplierName = concat('Name', supplierName, invoiceFields?.supplier?.userDetail?.companyName);
		supplierName = concat('ABN', supplierName, invoiceFields?.supplier?.userDetail?.companyAbn);

		return supplierName;
	};

	const supplierFieldValue = getSupplierName() || '';

	return (
		<>
			<SupplierSearchModal
				isOpen={open}
				officeEntityId={officeEntityId}
				onClose={() => setOpen(false)}
				selectedValue={invoiceFields.supplier}
				onSelect={(entity: UserServiceContractsDtosUserProfileDto) => {
					setInvoiceFields({ ...invoiceFields, supplier: entity });
					setOpen(false);
				}}
			/>
			<Table>
				<TableBody>
					<TableRow>
						<TableCell>
							<Typography variant="h4">Invoice Details</Typography>
						</TableCell>
					</TableRow>
					<TableRowInput
						onClick={() => setOpen(true)}
						placeholder="Supplier name"
						aria-label="supplier name"
						contentEditable={false}
						value={supplierFieldValue}
					/>
					{/* <TableRowInput
						onChange={onFieldChange('billerCode')}
						placeholder="Biller code"
						aria-label="biller code"
						value={invoiceFields.billerCode || ''}
					/>
					<TableRowInput onChange={onFieldChange('CRN')} placeholder="CRN" aria-label="crn" value={invoiceFields.CRN || ''} /> */}
					<TableRowInput
						onChange={onFieldChange('dueDate')}
						type="date"
						placeholder="Due Date"
						aria-label="due date"
						value={invoiceFields.dueDate || ''}
					/>
					<TableRowInput
						onChange={onFieldChange('totalAmount')}
						type="number"
						placeholder="Total amount"
						aria-label="total amount"
						value={invoiceFields.totalAmount?.toString() || ''}
					/>
					<TableRowInput
						onChange={onFieldChange('amount')}
						type="number"
						placeholder="Amount"
						aria-label="amount"
						value={invoiceFields.amount?.toString() || ''}
					/>
					<TableRowInput
						onChange={onFieldChange('GST')}
						type="number"
						placeholder="GST"
						aria-label="gst"
						value={invoiceFields.GST?.toString() || ''}
					/>
				</TableBody>
			</Table>
		</>
	);
};
